import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, useTheme, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import TableViewport from '../common/TableViewport';

const DRIVER_OPTS = ['revenue','units','orders','manual'];

export default function CostCentersAdminDialog({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState([]);
  const [, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await axios.get('/api/fca/cost_centers');
    setRows(res.data || []);
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const addRow = () => {
    setRows(prev => [
      ...prev,
      { id: null, name: '', category: '', default_driver: 'revenue', active: true, _dirty: true }
    ]);
  };

  const markDirty = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch, _dirty: true };
    setRows(next);
  };

  const saveRow = async (i) => {
    const r = rows[i];
    if (!r.name?.trim()) return;
    const payload = {
      id: r.id || undefined,
      name: r.name.trim(),
      category: r.category || '',
      default_driver: r.default_driver || 'revenue',
      active: !!r.active
    };
    const res = await axios.post('/api/fca/cost_centers', payload);
    const id = res.data?.id || r.id;
    const next = [...rows];
    next[i] = { ...r, id, _dirty: false };
    setRows(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Manage Cost Centers</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            Create, edit or deactivate cost centers. Driver controls how this center is allocated.
          </Typography>
          <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined">Add</Button>
        </Box>

        <TableViewport>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{width: 260}}>Name</TableCell>
              <TableCell style={{width: 180}}>Category</TableCell>
              <TableCell style={{width: 160}}>Driver</TableCell>
              <TableCell align="center" style={{width: 110}}>Active</TableCell>
              <TableCell align="right" style={{width: 90}}>Save</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id ?? `new-${i}`}>
                <TableCell>
                  <TextField
                    value={r.name || ''}
                    onChange={(e)=>markDirty(i,{name:e.target.value})}
                    size="small" fullWidth placeholder="e.g., Google Ads"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={r.category || ''}
                    onChange={(e)=>markDirty(i,{category:e.target.value})}
                    size="small" fullWidth placeholder="Marketing / Facilities / …"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    size="small" fullWidth value={r.default_driver || 'revenue'}
                    onChange={(e)=>markDirty(i,{default_driver:e.target.value})}
                  >
                    {DRIVER_OPTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={!!r.active}
                    onChange={(e)=>markDirty(i,{active:e.target.checked})}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={r._dirty ? 'Save changes' : 'Saved'}>
                    <span>
                      <IconButton onClick={()=>saveRow(i)} disabled={!r._dirty}
                        aria-label={r._dirty ? 'Save changes' : 'Saved'}>
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableViewport>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
